/**
 * Presentación del tablero de planificación: textos en español y clases de color.
 *
 * Vive fuera del componente y es PURO (sin React y, sobre todo, **sin `Date`**): el día de
 * semana se calcula sobre la cadena `YYYY-MM-DD`, así que ningún navegador puede correr la
 * columna un día. `new Date('2026-01-01')` se interpreta en UTC y en Lima (-05:00) se leería
 * como 31 de diciembre: ese error movería TODO el encabezado de la matriz.
 */

import type { Celda, EstadoCelda, Metrica, Tablero } from './planningBoard';

const FORMATO_FECHA = /^\d{4}-\d{2}-\d{2}$/;

/** Iniciales de la semana empezando en domingo, como las nombra el coordinador: L M M J V S D. */
const INICIALES = ['D', 'L', 'M', 'M', 'J', 'V', 'S'];

const NOMBRES_DIA = [
  'domingo', 'lunes', 'martes', 'miércoles', 'jueves', 'viernes', 'sábado',
];

const NOMBRES_MES = [
  'enero', 'febrero', 'marzo', 'abril', 'mayo', 'junio',
  'julio', 'agosto', 'setiembre', 'octubre', 'noviembre', 'diciembre',
];

/** Corrimiento de Sakamoto por mes: convierte el año-mes en un desplazamiento de semana. */
const CORRIMIENTO_MES = [0, 3, 2, 5, 0, 3, 5, 1, 4, 6, 2, 4];

function partes(fecha: string): { anio: number; mes: number; dia: number } {
  if (!FORMATO_FECHA.test(fecha)) {
    throw new Error(`Fecha inválida: "${fecha}". Se espera YYYY-MM-DD.`);
  }
  return {
    anio: Number(fecha.slice(0, 4)),
    mes: Number(fecha.slice(5, 7)),
    dia: Number(fecha.slice(8, 10)),
  };
}

/** Día de semana: 0 = domingo … 6 = sábado. Aritmética pura sobre la cadena (Sakamoto). */
export function diaDeSemana(fecha: string): number {
  const { anio, mes, dia } = partes(fecha);
  // Enero y febrero se cuentan como meses 13 y 14 del año anterior: así el 29 de febrero
  // queda al final del año y la regla del bisiesto no desfasa el resto del calendario.
  const y = mes < 3 ? anio - 1 : anio;
  const bisiestos = Math.floor(y / 4) - Math.floor(y / 100) + Math.floor(y / 400);
  return (y + bisiestos + CORRIMIENTO_MES[mes - 1] + dia) % 7;
}

/** Inicial del día de semana para el encabezado de la columna. */
export const inicialDiaSemana = (fecha: string): string => INICIALES[diaDeSemana(fecha)];

/** Sábado o domingo: la columna se atenúa, pero se muestra (hay campañas de fin de semana). */
export function esFinDeSemana(fecha: string): boolean {
  const d = diaDeSemana(fecha);
  return d === 0 || d === 6;
}

/** Número del día, sin cero a la izquierda: la columna dice "1", no "01". */
export const numeroDeDia = (fecha: string): number => partes(fecha).dia;

function nombreMes(mes: number): string {
  if (!Number.isInteger(mes) || mes < 1 || mes > 12) {
    throw new Error(`Mes inválido: ${mes}. Se espera un número de 1 a 12.`);
  }
  return NOMBRES_MES[mes - 1];
}

/** "agosto 2026": así se nombra el mes en los títulos y en los mensajes de vacío. */
export const etiquetaMes = (anio: number, mes: number): string =>
  `${nombreMes(mes)} ${anio}`;

/** "martes 11 de agosto de 2026": encabezado del detalle de una celda. */
export function fechaLarga(fecha: string): string {
  const { anio, mes, dia } = partes(fecha);
  return `${NOMBRES_DIA[diaDeSemana(fecha)]} ${dia} de ${nombreMes(mes)} de ${anio}`;
}

const plural = (n: number, singular: string, muchos: string): string =>
  `${n} ${n === 1 ? singular : muchos}`;

/**
 * Subtítulo de la fila, en el formato del tablero que el usuario ya usa:
 * "prog: 466 días-tienda · 25 días · logrado: 15 · 3%".
 *
 * "día-tienda" es la unidad de lo programado (una posición = una tienda en un día en un
 * turno) y "días" son los días del mes en que esa campaña trabaja: 466 puestos repartidos
 * en 25 días no es lo mismo que 466 en 3.
 */
export function resumenFila(fila: {
  totalProgramado: number;
  diasConProgramacion: number;
  totalLogrado: number;
  porcentaje: number;
}): string {
  return [
    `prog: ${plural(fila.totalProgramado, 'día-tienda', 'días-tienda')}`,
    plural(fila.diasConProgramacion, 'día', 'días'),
    `logrado: ${fila.totalLogrado}`,
    `${fila.porcentaje}%`,
  ].join(' · ');
}

/**
 * Lo que se pinta dentro de la celda. La celda programada SIEMPRE lleva "logrado/programado":
 * 0/20 es mucho más grave que 0/2 y con el color solos no se distinguen.
 * La celda sin programación va vacía a propósito: un "0/0" se leería como un hueco.
 */
export const etiquetaCelda = (celda: Celda): string =>
  (celda.programado <= 0 ? '' : `${celda.logrado}/${celda.programado}`);

export interface EstiloCelda {
  /** Nombre del estado en palabras: la leyenda no depende del color. */
  palabra: string;
  /** Qué significa, para la leyenda. */
  explicacion: string;
  /** Clases de fondo, texto y borde (contraste AA sobre el número de la celda). */
  clase: string;
  /** Cuadrito de color de la leyenda. */
  claseMuestra: string;
}

export const ESTILO_CELDA: Record<EstadoCelda, EstiloCelda> = {
  sin_programacion: {
    palabra: 'Sin programación',
    explicacion: 'ese día la campaña no trabaja',
    clase: 'bg-gray-100 text-gray-400',
    claseMuestra: 'bg-gray-200 border border-gray-300',
  },
  nadie: {
    palabra: 'Programado y nadie',
    explicacion: 'hay puestos y ninguno logrado',
    clase: 'bg-red-200 text-red-900 font-semibold',
    claseMuestra: 'bg-red-200 border border-red-400',
  },
  parcial: {
    palabra: 'Parcial',
    explicacion: 'falta cubrir parte de los puestos',
    clase: 'bg-amber-200 text-amber-900 font-semibold',
    claseMuestra: 'bg-amber-200 border border-amber-400',
  },
  completo: {
    palabra: 'Completo',
    explicacion: 'todos los puestos logrados',
    clase: 'bg-green-200 text-green-900 font-semibold',
    claseMuestra: 'bg-green-200 border border-green-400',
  },
};

/** Orden de la leyenda: de lo que no exige nada a lo que ya está resuelto. */
export const ORDEN_LEYENDA: EstadoCelda[] = [
  'sin_programacion', 'nadie', 'parcial', 'completo',
];

export interface DescripcionMetrica {
  titulo: string;
  /** Qué significa "logrado" con esta métrica. */
  logrado: string;
  /** La pregunta de operación que responde. */
  pregunta: string;
  /** Cuándo sirve mirarla. */
  cuando: string;
}

export const METRICAS: Record<Metrica, DescripcionMetrica> = {
  asignacion: {
    titulo: 'Asignación',
    logrado: 'logrado = puestos con asignación aprobada',
    pregunta: '¿tengo gente puesta en todos los puestos?',
    cuando: 'sirve ANTES del día, para detectar huecos a tiempo',
  },
  asistencia: {
    titulo: 'Asistencia',
    logrado: 'logrado = puestos cuyo recurso marcó ingreso',
    pregunta: '¿la gente que puse fue?',
    cuando: 'sirve durante y después del día',
  },
};

/**
 * Columnas a mostrar. Con `ocultarSinProgramacion` se van los días en que NINGUNA campaña
 * tiene programación (feriados, días de descanso): la matriz se angosta y lo que queda es
 * operación real. Nunca reordena: el mes se lee del 1 al 31.
 */
export function diasVisibles(
  tablero: Pick<Tablero, 'dias' | 'totalesPorDia'>,
  ocultarSinProgramacion: boolean,
): string[] {
  if (!ocultarSinProgramacion) return tablero.dias;
  return tablero.dias.filter((d) => (tablero.totalesPorDia[d]?.programado ?? 0) > 0);
}
